import { useEffect } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@shared/services/api/apiRequest";
import { usePlatform } from "@platform/PlatformContext";
import {
  NOTIFICATIONS_SET,
  NOTIFICATIONS_SET_LOADING,
  NOTIFICATIONS_SET_REFETCHING,
  useNotificationsDispatch,
  useNotificationsState,
} from "../NotificationsContext";
import { NotificationListResponse } from "../notifications.types";
import {
  NOTIFICATIONS_API,
  NOTIFICATIONS_PAGE_SIZE,
} from "../notifications.constants";

export type Args = {
  isEnabled?: boolean;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
};

export function useFetchNotifications(
  {
    isEnabled = true,
    refetchOnWindowFocus = true,
    refetchOnReconnect = true,
  }: Args = {
    isEnabled: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  }
) {
  const { page } = useNotificationsState();

  const dispatch = useNotificationsDispatch();

  const { user } = usePlatform();

  // Fetch notifications with react-query
  const query = useQuery<NotificationListResponse, Error>({
    queryKey: ["notifications", page],
    queryFn: async () => {
      return apiRequest(
        `${NOTIFICATIONS_API}?page=${page}&page_size=${NOTIFICATIONS_PAGE_SIZE}`,
        "GET"
      ) as Promise<NotificationListResponse>;
    },
    select: (data) => {
      return data;
    },
    // Only enable the query when user is logged in
    enabled: !!user && isEnabled,
    // Poll every 10 seconds when user is logged in
    refetchInterval: user ? 60000 : false,

    placeholderData: keepPreviousData,

    refetchOnWindowFocus: refetchOnWindowFocus,

    refetchOnReconnect: refetchOnReconnect,
  });

  useEffect(() => {
    if (query.isLoading) {
      dispatch({
        type: NOTIFICATIONS_SET_LOADING,
        payload: { isLoading: true },
      });
    } else {
      dispatch({
        type: NOTIFICATIONS_SET_LOADING,
        payload: { isLoading: false },
      });
    }
  }, [query.isLoading]);

  useEffect(() => {
    if (query.isRefetching) {
      dispatch({
        type: NOTIFICATIONS_SET_REFETCHING,
        payload: { isRefetching: true },
      });
    } else {
      dispatch({
        type: NOTIFICATIONS_SET_REFETCHING,
        payload: { isRefetching: false },
      });
    }
  }, [query.isRefetching]);

  // Set notifications in context
  useEffect(() => {
    if (query.data) {
      dispatch({
        type: NOTIFICATIONS_SET,
        payload: {
          notifications: query.data.notifications,
          unreadCount: query.data.unread_count,
          hasMore: query.data.has_more,
          loading: false,
        },
      });
    }
  }, [query.data]);

  return query;
}

export default useFetchNotifications;
